import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { withGilt, withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import { exportCSV } from 'utils/converters/csv.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import linkTo from 'utils/links/linkTo.util';

import { changeCommissionRate, createCommissionRate, getAllCommissionReport, getCommissionReport, setGlobalDiscount } from 'reports/actions';
import RevenueCommissionTableContainer from 'reports/Commissions/RevenueCommissionTable';
import { createAllCsvData } from 'reports/Commissions/utils';
import HeaderMenu from 'reports/Common/HeaderMenu';
import ReportsMainLayoutContainer from 'reports/Common/ReportsMainLayout';
import { Tables } from 'reports/consts';

import CN from './CommissionsLayout.module.scss';

const ReportsProps = ['salesReport'];

class CommissionsLayout extends ReportsMainLayoutContainer {
  constructor(props) {
    super(props);
    this.state = {
      headerData: {
        name: props.t('businessReports.commissions.title'),
        items: [
          {
            id: Tables.CountOfServices,
            name: props.t('businessReports.commissions.countsOfServices'),
            onClick: () => {
              this.setTables(Tables.CountOfServices);
            },
          },
          {
            id: Tables.CategoryServiceAndProduct,
            name: props.t('businessReports.commissions.staffCommissionCalculator'),
            onClick: () => {
              this.setTables(Tables.CategoryServiceAndProduct);
            },
          },
        ],
      },
      title: '',
      shownTables: 0,
    };
  }

  UNSAFE_componentWillMount() {
    this.setTables(Tables.CategoryServiceAndProduct);
  }

  exportAllStaffCommissionCsv = () => {
    const { salesReport, allCommissionReport, t, currency } = this.props;
    const stylists = salesReport.lastRequestResponse?.stylist_group ?? [];
    const commissionRates = allCommissionReport.lastRequestResponse ?? {};
    const csvData = createAllCsvData(stylists, commissionRates, t, currency);

    if (fromAnyToBool(salesReport.lastRequestResponse?.stylist_group?.length) && !csvData?.length) return;

    exportCSV({ data: csvData, fileName: t('businessReports.commissions.csvAllStaffRevenueShareFileName') });
  };

  createCommissionRate = (stylistId, data) => void this.props.createCommissionRate(this.props.salonId, stylistId, data);
  changeCommissionRate = (stylistId, data) => void this.props.changeCommissionRate(this.props.salonId, stylistId, data);
  getCommissionReport = (stylistId) => void this.props.getCommissionReport(this.props.salonId, stylistId);
  setGlobalDiscount = (discount) => void this.props.setGlobalDiscount(this.props.salonId, discount);
  checkIfAllDataIsLoaded = () => ReportsProps.every((prop) => this.props[prop].lastRequestResponse);

  render() {
    const { salonId, isDarkMode, formatDate, salesReport, isSalonInGB } = this.props;

    const linkCommissions = linkTo({ pattern: LINK.hrCommissions, params: { salonId } });

    const hasStaffCommissionData = fromAnyToBool(salesReport.lastRequestResponse?.stylist_group?.length);

    return (
      <div
        className={cn({
          'has-protipbox commissions': true,
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <div className="new-wraper">
          {!this.checkIfAllDataIsLoaded() && (
            <div className="spinner-container">
              <div className="load-spinner" />
            </div>
          )}
          <HeaderMenu data={this.state.headerData} selectedId={this.state.shownTables} />
          {this.props.salesReport.lastRequestResponse && (
            <div>
              <div className={CN.panel}>
                <div className={CN.flex}>
                  <div className={CN.panelContainer}>
                    <div className={CN.panelText}>
                      <span className={CN.extraBold}>
                        {S.lamp} {this.props.t('businessReports.commissions.proTip')}
                      </span>
                      {S.space}
                      {this.props.t('businessReports.commissions.proTipDescription1')}
                    </div>
                    {isSalonInGB && (
                      <div className={CN.panelText}>
                        <span className={CN.extraBold}>
                          {S.lamp} {this.props.t('businessReports.commissions.proTip')}
                        </span>
                        {S.space}
                        {this.props.t('businessReports.commissions.proTipDescription2')}
                      </div>
                    )}
                  </div>
                  <div className={CN.buttonsContainer}>
                    <Button variant="primaryForce">
                      <NavLink to={linkCommissions}>
                        <div className={CN.buttonText}>{this.props.t('businessReports.commissions.commissionsLink')}</div>
                      </NavLink>
                    </Button>
                    <Button variant="primaryForce" onClick={this.exportAllStaffCommissionCsv} disabled={!hasStaffCommissionData}>
                      <span className={CN.buttonText}>{this.props.t('businessReports.commissions.downloadAllReportsButton')}</span>
                    </Button>
                  </div>
                </div>
              </div>
              <RevenueCommissionTableContainer
                startDate={formatDate(this.props.uniqueVisitsReport.start_date, { hideTime: true, hideWeekday: true })}
                endDate={formatDate(this.props.uniqueVisitsReport.end_date, { hideTime: true, hideWeekday: true })}
                salonVatRate={this.props.salonVatRate}
                createCommissionRate={this.createCommissionRate}
                changeCommissionRate={this.changeCommissionRate}
                data={this.props.salesReport.lastRequestResponse.stylist_group}
                allCommissionReport={this.props.allCommissionReport.lastRequestResponse}
                getAllCommissionReport={this.props.getAllCommissionReport}
                salonId={this.props.salonId}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    currentSalon: { id: salonId },
    reports,
  } = state;
  const { uniqueVisitsReport, salesReport, commissionReport, globalDiscount, salonVatRate, allCommissionReport, isFetching } = reports;
  return {
    uniqueVisitsReport,
    salonId,
    salesReport,
    salonVatRate,
    commissionReport,
    allCommissionReport,
    isFetching,
    globalDiscount: globalDiscount.discount,
    currency: selectCurrentSalonCurrency(state),
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    setGlobalDiscount(salonId, globalDiscount) {
      dispatch(setGlobalDiscount(salonId, globalDiscount));
    },
    createCommissionRate(salonId, stylistId, data) {
      dispatch(createCommissionRate(salonId, stylistId, data));
    },
    changeCommissionRate(salonId, stylistId, data) {
      dispatch(changeCommissionRate(salonId, stylistId, data));
    },
    getCommissionReport(salonId, stylistId) {
      dispatch(getCommissionReport(salonId, stylistId));
    },
    getAllCommissionReport(salonId) {
      dispatch(getAllCommissionReport(salonId));
    },
  };
}

const CommissionsLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTheme()(withGilt(withTranslation()(CommissionsLayout))));

export default CommissionsLayoutHoc;
